import hljs from 'highlight.js';

export enum FormatTreeNodeType {
  ROOT,
  TEXT,
  BOLD,
  ITALIC,
  STRIKE,
  CODE,
  CODEBLOCK,
  LINK,
  QUOTE,
  HIDDEN,
}

type Options = {
  enableSyntaxHighlighting: boolean;
  setInteractive: boolean;
  codeblockCopy: boolean;
};

type Formatter = {
  matcher: (c: string) => RegExpExecArray | null;
  left: (l: string, c: string, r: string) => string;
  content: (l: string, c: string, r: string, f: Options) => string;
  right: (l: string, c: string, r: string) => string;
  priority: number;
  nested: FormatTreeNodeType[];
};

const FORMATTERS: Record<FormatTreeNodeType, Formatter> = {
  [FormatTreeNodeType.ROOT]: {
    matcher: (c) => /.*/.exec(c),
    left: () => '',
    content: (l, c, r) => c,
    right: () => '',
    priority: -1,
    nested: [
      FormatTreeNodeType.CODEBLOCK,
      FormatTreeNodeType.CODE,
      FormatTreeNodeType.BOLD,
      FormatTreeNodeType.ITALIC,
      FormatTreeNodeType.STRIKE,
      FormatTreeNodeType.LINK,
      FormatTreeNodeType.QUOTE,
      FormatTreeNodeType.HIDDEN,
    ],
  },
  [FormatTreeNodeType.TEXT]: {
    matcher: (c) => /.*/.exec(c),
    left: () => '',
    content: (l, c, r) => c,
    right: () => '',
    priority: -1,
    nested: [],
  },
  [FormatTreeNodeType.BOLD]: {
    matcher: (c) => {
      const match =
        /(?:(\*\0?\*\0?)((?:[^*\n]+)|(?:[^*\n]*\*[^*\n]+\*[^*\n]*)|(?:[^*\n]+\*[^*\n]+))(\0?\*\0?\*))|(?:(?<![a-zA-Z0-9]\0?)(_\0?_\0?)(?!\0?\s)((?:[^_\n]+)|(?:[^_\n]*_[^_\n]+_[^_\n]*)|(?:[^_\n]+_[^_\n]+))(?<!\s\0?)(\0?_\0?_)(?!\0?[a-zA-Z0-9]))/g.exec(
          c
        );
      if (match && match[4]) {
        match[1] = match[4];
        match[2] = match[5];
        match[3] = match[6];
      }
      return match;
    },
    left: (l, c, r) => `<span class="controls">${l}</span><b>`,
    content: (l, c, r) => c,
    right: (l, c, r) => `</b><span class="controls">${r}</span>`,
    priority: 4,
    nested: [
      FormatTreeNodeType.CODE,
      FormatTreeNodeType.ITALIC,
      FormatTreeNodeType.STRIKE,
      FormatTreeNodeType.LINK,
      FormatTreeNodeType.HIDDEN,
    ],
  },
  [FormatTreeNodeType.ITALIC]: {
    matcher: (c) => {
      const match =
        /(?:(\*\0?)((?:[^*\n]*\*\0?\*[^*\n]+\*\0?\*[^*\n]*)|(?:[^*\n]+))(\0?\*))|(?:(?<![a-zA-Z0-9]\0?)(_\0?)(?!\0?\s)((?:[^_\n]*_\0?_[^_\n]+_\0?_[^_\n]*)|(?:[^_\n]+))(?<!\s\0?)(\0?_)(?!\0?[a-zA-Z0-9]))/g.exec(
          c
        );
      if (match && match[4]) {
        match[1] = match[4];
        match[2] = match[5];
        match[3] = match[6];
      }
      return match;
    },
    left: (l, c, r) => `<span class="controls">${l}</span><i>`,
    content: (l, c, r) => c,
    right: (l, c, r) => `</i><span class="controls">${r}</span>`,
    priority: 5,
    nested: [
      FormatTreeNodeType.CODE,
      FormatTreeNodeType.BOLD,
      FormatTreeNodeType.STRIKE,
      FormatTreeNodeType.LINK,
      FormatTreeNodeType.HIDDEN,
    ],
  },
  [FormatTreeNodeType.STRIKE]: {
    matcher: (c) => /(~\0?~)(\0?[^\s\0](?:.*?[^\s\0]\0?)??)(~\0?~)/g.exec(c),
    left: (l, c, r) => `<span class="controls">${l}</span><s>`,
    content: (l, c, r) => c,
    right: (l, c, r) => `</s><span class="controls">${r}</span>`,
    priority: 6,
    nested: [
      FormatTreeNodeType.CODE,
      FormatTreeNodeType.BOLD,
      FormatTreeNodeType.ITALIC,
      FormatTreeNodeType.LINK,
      FormatTreeNodeType.HIDDEN,
    ],
  },
  [FormatTreeNodeType.CODE]: {
    // Single-backtick spans must not consume part of an unfinished fence.
    matcher: (c) => /(?<!`\0?)(`)(\0?[^\s\0`](?:[^`\r\n]*?[^\s\0`])?\0?)(`)(?!\0?`)/g.exec(c),
    left: (l, c, r) => `<pre><span class="controls">${l}</span>`,
    content: (l, c, r) => c,
    right: (l, c, r) => `<span class="controls">${r}</span></pre>`,
    priority: 3,
    nested: [],
  },
  [FormatTreeNodeType.CODEBLOCK]: {
    matcher: (c) =>
      /^(\0?`\0?`\0?`(?:[^\n]*\n)?)((?:.|\n)+?)^(\0?`\0?`\0?`(?:\s|\0)*(?:\n|$))/gm.exec(c),
    left: (l, c, r) => `<pre class="block hljs"><span class="controls">${l}</span>`,
    content: (l, c, r, f) => {
      let prefix = '<span class="scroller">';
      const suffix = '</span>';
      const copyButton = '<button type="button" class="copy"></button>';

      if (f.codeblockCopy) {
        prefix = copyButton + prefix;
      }

      if (!f.enableSyntaxHighlighting) return prefix + c + suffix;

      // TODO(jul14n): evaluate whether @lezer can be used here
      // to get even better syntax highlighting results

      const lang = l.slice(3).toLowerCase().trim();
      c = c.replaceAll('&gt;', '>').replaceAll('&lt;', '<').replaceAll('&amp;', '&');
      if (!lang || !hljs.getLanguage(lang)) {
        return prefix + hljs.highlightAuto(c).value + suffix;
      }

      return prefix + hljs.highlight(c, { language: lang }).value + suffix;
    },
    right: (l, c, r) =>
      `<span class="controls">${r.trim()}</span></pre>${r.endsWith('\n') ? '\n' : ''}`,
    priority: 0,
    nested: [],
  },
  [FormatTreeNodeType.LINK]: {
    matcher: (c) =>
      /((?:^|\s)\0?)(\0?h\0?t\0?t\0?p\0?s?\0?:\0?\/\0?\/\0?(?:w\0?w\0?w\0?\.\0?)?[-\0a-zA-Z0-9@:%._+~#=]{1,257}\0?\.\0?[\0a-zA-Z0-9()]{1,7}\b[^\s]*)($|\s)/g.exec(
        c
      ),
    left: (l, c, r) =>
      `${l}<a href="${c.replaceAll('\0', '').replaceAll('"', '%22')}" target="_blank">`,
    content: (l, c, r) => c,
    right: (l, c, r) => `</a>${r}`,
    priority: 2,
    nested: [],
  },
  [FormatTreeNodeType.QUOTE]: {
    matcher: (c) => /^(\0?&gt;\0?(?:\s))(.*)($|\0)/gm.exec(c),
    left: (l, c, r) => `<span class="quote_indicator">${l}</span>`,
    content: (l, c, r) => c,
    right: (l, c, r) => '',
    priority: 1,
    nested: [
      FormatTreeNodeType.BOLD,
      FormatTreeNodeType.ITALIC,
      FormatTreeNodeType.STRIKE,
      FormatTreeNodeType.CODE,
      FormatTreeNodeType.LINK,
      FormatTreeNodeType.HIDDEN,
    ],
  },
  [FormatTreeNodeType.HIDDEN]: {
    matcher: (c) => /(\|\0?\|)(.*?)??(\|\0?\|)/g.exec(c),
    left: (l, c, r) => `<span class="controls">${l}</span>`,
    content: (l, c, r, f) => {
      let a = '<span class="hidden">';
      if (f.setInteractive) {
        a = '<span class="hidden" onclick="this.classList.add(\'revealed\')">';
      }
      return a + c + '</span>';
    },
    right: (l, c, r) => `<span class="controls">${r}</span></span>`,
    priority: 6,
    nested: [
      FormatTreeNodeType.CODE,
      FormatTreeNodeType.BOLD,
      FormatTreeNodeType.ITALIC,
      FormatTreeNodeType.LINK,
    ],
  },
};

export class FormatTreeNode {
  private type: FormatTreeNodeType;

  private left?: FormatTreeNode;
  private child?: FormatTreeNode;
  private right?: FormatTreeNode;

  private content: string;

  private match?: RegExpExecArray;

  constructor(
    content: string,
    type: FormatTreeNodeType = FormatTreeNodeType.ROOT,
    match?: RegExpExecArray
  ) {
    this.type = type;
    this.content = content;
    this.match = match;
    if (this.type !== FormatTreeNodeType.TEXT) this.processContent();
  }

  private processContent() {
    const formatters = FORMATTERS[this.type].nested;
    const potentialMatches = [];
    for (const format of formatters) {
      const match = FORMATTERS[format].matcher(this.content);
      if (!match) continue;
      potentialMatches.push({ match, format });
    }
    if (potentialMatches.length === 0) {
      this.child = new FormatTreeNode(this.content, FormatTreeNodeType.TEXT);
      return;
    }
    const match = potentialMatches.reduce((prev, curr) =>
      prev.match.index === curr.match.index
        ? FORMATTERS[prev.format].priority < FORMATTERS[curr.format].priority
          ? prev
          : curr
        : prev.match.index < curr.match.index
          ? prev
          : curr
    );

    this.left = new FormatTreeNode(
      this.content.slice(0, match.match.index),
      FormatTreeNodeType.ROOT
    );

    this.right = new FormatTreeNode(
      this.content.slice(match.match.index + match.match[0].length),
      FormatTreeNodeType.ROOT
    );

    this.child = new FormatTreeNode(match.match[2], match.format, match.match);
  }

  public assembleString(options: Options) {
    if (this.type === FormatTreeNodeType.TEXT) return this.content;

    let content = '';
    content += this.left?.assembleString(options) || '';
    content += this.child?.assembleString(options) || '';
    content += this.right?.assembleString(options) || '';

    if (!this.match) return content;

    let result = '';

    result += FORMATTERS[this.type].left(this.match[1], content, this.match[3]);
    result += FORMATTERS[this.type].content(this.match[1], content, this.match[3], options);
    result += FORMATTERS[this.type].right(this.match[1], this.match[2], this.match[3]);
    return result;
  }

  equalsInStructure(other: FormatTreeNode): boolean {
    if (this.type !== other.type) return false;
    if (
      !!this.left !== !!other.left ||
      (this.left && other.left && !this.left.equalsInStructure(other.left))
    ) {
      return false;
    }
    if (
      !!this.child !== !!other.child ||
      (this.child && other.child && !this.child.equalsInStructure(other.child))
    ) {
      return false;
    }
    if (
      !!this.right !== !!other.right ||
      (this.right && other.right && !this.right.equalsInStructure(other.right))
    ) {
      return false;
    }

    return true;
  }
}

export const buildFormatTreeFromString = (str: string) => {
  return new FormatTreeNode(str);
};
